import { Component } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationEnd,
  Router,
} from '@angular/router';
import { filter } from 'rxjs';

import { AuthService } from '../../services/auth.service';
import { SidenavStateService } from '../../services/sidenav-state.service';

interface NavigationItem {
  label: string;
  icon: string;
  route: string;
}

interface NavigationSection {
  label: string;
  icon: string;
  expanded: boolean;
  items: NavigationItem[];
}

@Component({
  standalone: false,
  selector: 'sidenav',
  templateUrl: './sidenav.component.html',
  styleUrl: './sidenav.component.css',
})
export class SidenavComponent {
  isCollapsed = false;
  canViewDashboard = false;
  canViewConfiguration = false;
  signageSection: NavigationSection = {
    label: 'Signage',
    icon: 'pi pi-desktop',
    expanded: true,
    items: [],
  };

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly sidenavState: SidenavStateService,
  ) {
    this.authService.sessionUpdated$.pipe(
      takeUntilDestroyed(),
    ).subscribe(() => {
      this.signageSection = {
        ...this.signageSection,
        items: this.createSignageItems(),
      };
      this.canViewDashboard = this.authService.hasPermission(
        'signageDevices:read',
      );
      this.canViewConfiguration = this.hasConfigurationAccess();
    });

    this.sidenavState.isCollapsed$.pipe(
      takeUntilDestroyed(),
    ).subscribe((isCollapsed) => {
      this.isCollapsed = isCollapsed;
    });

    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(),
    ).subscribe(() => {
      this.closeMobileMenu();
    });
  }

  toggleSection(): void {
    this.signageSection.expanded = !this.signageSection.expanded;
  }

  toggleSideBar(): void {
    this.sidenavState.toggle();
  }

  closeMobileMenu(): void {
    this.sidenavState.closeMobile();
  }

  private createSignageItems(): NavigationItem[] {
    const items: NavigationItem[] = [];

    if (this.authService.hasPermission('signageDevices:read')) {
      items.push({
        label: 'Devices',
        icon: 'pi pi-desktop',
        route: '/devices',
      });
    }

    if (this.authService.hasPermission('reports:read')) {
      items.push({
        label: 'Reports',
        icon: 'pi pi-chart-bar',
        route: '/reports',
      });
    }

    return items;
  }

  private hasConfigurationAccess(): boolean {
    return [
      'users:read',
      'configuration:read',
    ].some((permission) => this.authService.hasPermission(permission));
  }
}
